<script lang="ts">
    import { onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faCircleCheck } from '@fortawesome/free-solid-svg-icons'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import FormFill from '../customer-service-v2/forms/form-fill.svelte'
    import FormV2Print from '../customer-service-v2/forms/form-v2-print.svelte'
    import { createDefaultHeaderFooter } from '../customer-service-v2/types/document-header-footer'
    import { readContextSnapshot, type FillContext } from '../customer-service-v2/forms/context-snapshot'
    import type { FormV2Form } from '../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'

    const DRAFT = 60
    const COMPLETED = 100
    const SIGNING = 105
    const SIGNED = 110
    const MODE_LINK = 1
    const MODE_EMAIL = 2

    // O yrv trata rotas não-"exact" como prefixo, então "/public/forms-v2" também "casa" com
    // "/public/forms-v2/print" e "/public/forms-v2/assinar" — ambos os componentes acabariam
    // montados ao mesmo tempo. (Não dá pra usar `exact` aqui: nesse router o comparador de `exact`
    // usa a URL crua, com query string incluída, então nunca bate com "/public/forms-v2?id=...".)
    // Guarda manual pelo pathname exato em vez disso.
    const isThisRoute = location.pathname.replace(/\/$/, '') === '/public/forms-v2'

    let show = true
    let error = ''
    let form: FormV2Form = null
    let formV2FormLinkId = ''
    let token = ''
    let saving = false
    let savedMessage = ''
    let submitted = false
    // Congelado dentro do documento no último salvamento pelo editor logado (ver context-snapshot.ts).
    // Substitui as tags {} do texto fixo ({nome}, {compra}, {vendedor}, {atendente}, {usuario}).
    // Antes cada dado precisava de uma resolução própria no backend, porque esta página não tem sessão
    // para buscar nada — e o que ninguém lembrasse de resolver saía em branco.
    let fillContext: FillContext = {}

    // Fluxo de assinatura ao concluir preenchimento (template com "Solicitar assinatura ao preencher").
    let signingStage: 'idle' | 'starting' | 'redirecting' | 'email-sent' | 'error' = 'idle'
    let signingEmail = ''
    let signingError = ''
    // Link de assinatura da própria pessoa, buscado só quando o documento reabre já em SIGNING
    // (modalidade link) — usado pelo botão "Concluir assinatura".
    let continueSignUrl = ''

    $: signatureRequested = !!(form?.document as any)?.templateSnapshot?.headerFooter?.signature?.requested

    function expandToken(id: string) {
        const parts = id.split('_')
        return {
            formV2FormLinkId: window.api.utils.conversions.base58ToUuid(parts[0]),
            token: window.api.utils.conversions.base58ToUuid(parts[1]),
        }
    }

    onMount(() => {
        if (isThisRoute) load()
    })

    async function load() {
        try {
            show = true
            const urlParams = new URLSearchParams(window.location.search)
            const id = urlParams.get('id')
            if (!id || !id.includes('_')) {
                error = 'Link inválido.'
                return
            }
            const expanded = expandToken(id)
            formV2FormLinkId = expanded.formV2FormLinkId
            token = expanded.token
            const result = await window.api.tools.formsV2.forms.publicGetOne(formV2FormLinkId, token)
            form = result.form
            window.api.users.statistics.tickPublicOnce(form.companyId, 'pub.form.open')
            fillContext = readContextSnapshot((form.document as any)?.contextSnapshot)
            if (form.status === SIGNING && form.signatureMode === MODE_LINK) {
                await loadContinueSigningLink()
            }
        } catch (err) {
            error = 'Este link não está mais disponível.\nEle pode estar errado ou o tempo de compartilhamento expirou.'
        } finally {
            show = false
        }
    }

    async function loadContinueSigningLink() {
        try {
            const result = await window.api.tools.formsV2.forms.publicGetContinueSigningLink(formV2FormLinkId, token)
            if (result) {
                continueSignUrl = window.api.utils.utils.domain() + '/public/forms-v2/assinar?id=' + result.formV2FormSignatureId + '&t=' + result.token
            }
        } catch (err) {
            // Sem link pra continuar não é um erro fatal — a mensagem genérica de "já está assinando" já cobre.
        }
    }

    function extractExtensionFromDataUrl(dataUrl: string): string {
        const mime = dataUrl.match(/^data:([^;]+);/)?.[1] ?? ''
        const map: Record<string, string> = {
            'image/png': 'png',
            'image/jpeg': 'jpg',
            'image/gif': 'gif',
            'image/webp': 'webp',
            'image/bmp': 'bmp',
            'application/pdf': 'pdf',
        }
        return map[mime] ?? 'bin'
    }

    async function uploadBase64(dataUrl: string): Promise<string> {
        const { url } = await window.api.tools.formsV2.forms.publicUploadFile(formV2FormLinkId, token, dataUrl)
        return url
    }

    async function uploadFilesInDocument(doc: any): Promise<any> {
        const answers: Record<string, any> = {}
        for (const blockId of Object.keys(doc.answers)) {
            const value = doc.answers[blockId]
            const block = doc.templateSnapshot.blocks.find((b: any) => b.id === blockId)
            if (!block || !value) {
                answers[blockId] = value
                continue
            }

            if (block.type === 'sign' && typeof value === 'string' && value.startsWith('data:')) {
                answers[blockId] = await uploadBase64(value)
            } else if (block.type === 'image_annotation' && Array.isArray(value)) {
                answers[blockId] = await Promise.all(
                    value.map(async (item: any) => {
                        if (!item.dataUrl?.startsWith('data:')) return item
                        return { ...item, dataUrl: await uploadBase64(item.dataUrl) }
                    }),
                )
            } else if (block.type === 'file_upload' && Array.isArray(value)) {
                answers[blockId] = await Promise.all(
                    value.map(async (item: any) => {
                        if (!item.dataUrl?.startsWith('data:')) return item
                        return { ...item, dataUrl: await uploadBase64(item.dataUrl) }
                    }),
                )
            } else if (block.type === 'before_after' && value && typeof value === 'object') {
                const { before, after } = value
                answers[blockId] = {
                    before: before?.src?.startsWith('data:') ? { ...before, src: await uploadBase64(before.src) } : before,
                    after: after?.src?.startsWith('data:') ? { ...after, src: await uploadBase64(after.src) } : after,
                }
            } else {
                answers[blockId] = value
            }
        }
        return { ...doc, answers }
    }

    async function onSave(event: CustomEvent) {
        await persist(event.detail.document, DRAFT)
    }

    async function onSubmit(event: CustomEvent) {
        // Reenvio: o documento já estava "Concluído" (uma tentativa anterior de iniciar a assinatura
        // falhou) — os campos já estão travados e não há nada de novo pra salvar, então pula direto
        // pra tentar a assinatura de novo. O backend também bloquearia esse publicUpdate mesmo que a
        // gente tentasse (status >= COMPLETED não pode mais ser editado).
        if (form.status === COMPLETED) {
            await startSigningFlow()
            return
        }
        const ok = await persist(event.detail.document, COMPLETED)
        if (!ok) return
        if (signatureRequested) {
            await startSigningFlow()
        } else {
            submitted = true
        }
    }

    async function startSigningFlow() {
        try {
            signingStage = 'starting'
            const result = await window.api.tools.formsV2.forms.publicStartSigning(formV2FormLinkId, token)
            if (result.mode === MODE_EMAIL) {
                signingEmail = result.email || ''
                signingStage = 'email-sent'
            } else {
                signingStage = 'redirecting'
                location.href = window.api.utils.utils.domain() + '/public/forms-v2/assinar?id=' + result.formV2FormSignatureId + '&t=' + result.token
            }
        } catch (err: any) {
            // Cadastro de assinante incompleto (falta CPF/CNPJ ou, na modalidade e-mail, o e-mail) —
            // aqui não há uma tela de cadastro pra abrir (isso é uma ação interna do estabelecimento),
            // então só orienta a pessoa a entrar em contato em vez do erro genérico de "tente novamente".
            signingError =
                err?.code === 'MISSING_SIGNER_INFO'
                    ? 'Não foi possível iniciar a assinatura: há dados cadastrais pendentes. Entre em contato com o estabelecimento.'
                    : 'Não foi possível iniciar a assinatura. Tente novamente.'
            signingStage = 'error'
        }
    }

    async function persist(document: any, status: number): Promise<boolean> {
        if (saving) return false
        try {
            saving = true
            document = await uploadFilesInDocument(document)
            await window.api.tools.formsV2.forms.publicUpdate(formV2FormLinkId, token, { status, document })
            form = { ...form, document, status }
            if (status === DRAFT) savedMessage = 'Preenchimento salvo com sucesso!'
            // rascunho (DRAFT) não conta: só o preenchimento concluído é entrega de valor
            if (status === COMPLETED) window.api.users.statistics.tickPublic(form.companyId, 'pub.form.fill')
            return true
        } catch (err) {
            window.error(err)
            return false
        } finally {
            saving = false
        }
    }
</script>

{#if isThisRoute}
    <div class="root screen-only">
        <LoadSpinner show={show || signingStage === 'starting'}>
            {signingStage === 'starting'
                ? 'Estamos preparando seu documento para assinatura. Só um instante...'
                : signingStage === 'redirecting'
                  ? 'Redirecionando para a assinatura...'
                  : ''}
        </LoadSpinner>
        {#if error}
            <div class="msg">{error}</div>
        {:else if signingStage === 'starting' || signingStage === 'redirecting'}
            <!-- LoadSpinner acima já cobre a tela com a mensagem apropriada -->
        {:else if signingStage === 'email-sent'}
            <div class="success-screen">
                <div class="success-icon"><Fa icon={faCircleCheck} /></div>
                <div class="success-title">Verifique seu e-mail</div>
                <div class="success-subtitle">
                    Enviamos para o e-mail {signingEmail} o link para você assinar o contrato.<br />Acesse seu e-mail para concluir a assinatura.
                </div>
            </div>
        {:else if signingStage === 'error'}
            <div class="msg">{signingError}</div>
            <div class="retry-wrap">
                <button type="button" class="retry-btn" on:click={startSigningFlow}>Tentar novamente</button>
            </div>
        {:else if submitted}
            <div class="success-screen">
                <div class="success-icon"><Fa icon={faCircleCheck} /></div>
                <div class="success-title">Preenchimento enviado com sucesso!</div>
                <div class="success-subtitle">Obrigado, seu formulário foi recebido.</div>
            </div>
        {:else if form && form.status === SIGNING && form.signatureMode === MODE_EMAIL}
            <div class="msg">
                Este documento já está em processo de assinatura ou já foi assinado.<br />Verifique seu e-mail para concluir a assinatura.
            </div>
        {:else if form && form.status === SIGNING}
            <div class="msg">Este documento está em processo de assinatura.</div>
            {#if continueSignUrl}
                <div class="retry-wrap">
                    <a class="retry-btn" href={continueSignUrl}>Concluir assinatura</a>
                </div>
            {/if}
        {:else if form}
            <FormFill
                title={form.document?.templateSnapshot?.title ?? form.title}
                blocks={form.document?.templateSnapshot?.blocks ?? []}
                headerFooter={form.document?.templateSnapshot?.headerFooter ?? createDefaultHeaderFooter()}
                initialAnswers={form.document?.answers ?? {}}
                readOnlyAll={form.status >= COMPLETED}
                allowDraft={form.status < COMPLETED}
                showSignButton={form.status === COMPLETED && signatureRequested}
                companyId={form.companyId}
                context={fillContext}
                public={true}
                {signatureRequested}
                {saving}
                on:save={onSave}
                on:submit={onSubmit}
            />
            {#if form.status === SIGNED}
                <div class="signed-notice">
                    <Fa icon={faCircleCheck} />
                    <span>Este documento foi assinado digitalmente.</span>
                </div>
            {/if}
            {#if savedMessage}
                <div class="msg success">{savedMessage}</div>
            {/if}
        {/if}
    </div>

    {#if form}
        <!-- Só existe para a impressão nativa do navegador (Ctrl+P / @media print) na página pública —
         alterna com o conteúdo interativo acima, que fica oculto durante a impressão. -->
        <div class="print-only">
            <FormV2Print
                blocks={form.document?.templateSnapshot?.blocks ?? []}
                headerFooter={form.document?.templateSnapshot?.headerFooter ?? createDefaultHeaderFooter()}
                answers={form.document?.answers ?? {}}
                companyId={form.companyId}
                context={fillContext}
            />
        </div>
    {/if}
{/if}

<style>
    .root {
        min-height: 100vh;
        background: #f4f5f7;
        padding: 16px;
    }
    .msg {
        max-width: 1000px;
        margin: 40px auto;
        text-align: center;
        color: #495057;
        white-space: pre-line;
    }
    .retry-wrap {
        display: flex;
        justify-content: center;
    }
    .retry-btn {
        display: inline-block;
        border: none;
        border-radius: 6px;
        padding: 10px 22px;
        font-size: 0.9rem;
        font-weight: 600;
        background: #4263eb;
        color: #fff;
        text-decoration: none;
        cursor: pointer;
    }
    .retry-btn:hover {
        background: #364fc7;
    }
    .msg.success {
        max-width: 1000px;
        margin: 0 auto 24px;
        text-align: center;
        color: #2f9e44;
        font-weight: 600;
        white-space: normal;
    }
    .signed-notice {
        max-width: 1000px;
        margin: 16px auto 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #2f9e44;
        font-weight: 600;
    }
    .success-screen {
        max-width: 480px;
        margin: 80px auto;
        text-align: center;
        background: #fff;
        color: #212529;
        border-radius: 8px;
        padding: 40px 24px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    }
    .success-icon {
        font-size: 48px;
        color: #2f9e44;
        margin-bottom: 16px;
    }
    .success-title {
        font-size: 1.2rem;
        font-weight: 600;
        margin-bottom: 8px;
    }
    .success-subtitle {
        color: #868e96;
    }
    .print-only {
        display: none;
    }
    @media print {
        .screen-only {
            display: none !important;
        }
        .print-only {
            display: block !important;
        }
    }
</style>
